// Admurin paperdoll layer definitions.
//
// Each layer has:
//   slot       — config key in token.paperdoll.layers
//   folder     — folder under public/paperdolls/
//   tag        — in-filename tag (Base/Body/Bottom/Top/Hair/Cape/1H/...)
//   prefix     — literal prefix baked into the filename ('M', 'F', 'B', 'L'),
//                or null for no prefix. The pack is M-only for gendered body
//                layers (Body/Top/Chest/Overall); we standardise on M.
//   anims      — animations this layer supports
//   z          — back-to-front draw order
//
// Filename: `<prefix?>_<anim>_<tag>[_<variant>].png` (underscore-joined).
//
// Weapons (OneHanded / Bow / Staff / Cape / Offhand / Quiver / Artifact) ship
// two sheets per variant — B_ (behind body) and F_ (in front). Both layers
// draw every frame at the same variant key; the pack's per-direction rows
// handle which parts are visible from each angle.

import { VARIANTS as GENERATED_VARIANTS } from './paperdollVariants';

export const ANIMS = ['Idl', 'Mov', 'Mel', 'Mag', 'Ran'];
export const DIRS = ['down', 'left', 'right', 'up'];
export const FRAMES_PER_ANIM = { Idl: 4, Mov: 6, Mel: 6, Mag: 6, Ran: 6 };

export const LAYERS = [
  // ── Behind body ──────────────────────────────────────────────────────────
  { slot: 'quiver',   folder: 'Quiver',    tag: 'Quiver',   prefix: 'B', anims: ['Idl','Mov','Ran'], z: -20 },
  { slot: 'ranged',   folder: 'Bow',       tag: 'Bow',      prefix: 'B', anims: ['Idl','Mov','Ran'], z: -19 },
  { slot: 'magic',    folder: 'Staff',     tag: 'Staff',    prefix: 'B', anims: ['Idl','Mov','Mag'], z: -18 },
  { slot: 'melee',    folder: 'OneHanded', tag: '1H',       prefix: 'B', anims: ['Idl','Mov','Mel'], z: -17 },
  { slot: 'offhand',  folder: 'Offhand',   tag: 'Offhand',  prefix: 'B', anims: ['Idl','Mov','Mel'], z: -16 },
  { slot: 'artifact', folder: 'Artifact',  tag: 'Artifact', prefix: 'B', anims: ['Idl','Mov','Mag'], z: -15 },
  { slot: 'cape',     folder: 'Cape',      tag: 'Cape',     prefix: 'B', anims: ['Idl','Mov','Mel','Mag','Ran'], z: -10 },

  // ── Body ────────────────────────────────────────────────────────────────
  // Z-order runs naked body → clothing → accessories. Clothing sprites
  // occupy only the garment pixels, so they must draw AFTER the body.
  { slot: 'body',     folder: 'Body',      tag: 'Body',     prefix: 'M',  anims: ['Idl','Mov','Mel','Mag','Ran'], z: 1 },
  { slot: 'bottom',   folder: 'Bottom',    tag: 'Bottom',   prefix: null, anims: ['Idl','Mov','Mel','Mag','Ran'], z: 2 },
  { slot: 'feet',     folder: 'Feet',      tag: 'Feet',     prefix: null, anims: ['Idl','Mov','Mel','Mag','Ran'], z: 3 },
  { slot: 'top',      folder: 'Top',       tag: 'Top',      prefix: 'M',  anims: ['Idl','Mov','Mel','Mag','Ran'], z: 4 },
  { slot: 'overall',  folder: 'Overall',   tag: 'Overall',  prefix: 'M',  anims: ['Idl','Mov','Mel','Mag','Ran'], z: 4.5 },
  { slot: 'chest',    folder: 'Chest',     tag: 'Chest',    prefix: 'M',  anims: ['Idl','Mov','Mel','Mag','Ran'], z: 5 },
  { slot: 'hands',    folder: 'Hands',     tag: 'Hands',    prefix: null, anims: ['Idl','Mov','Mel','Mag','Ran'], z: 6 },
  { slot: 'hair',     folder: 'Hair',      tag: 'Hair',     prefix: null, anims: ['Idl','Mov','Mel','Mag','Ran'], z: 7 },
  { slot: 'head',     folder: 'Head',      tag: 'Head',     prefix: null, anims: ['Idl','Mov','Mel','Mag','Ran'], z: 8 },
  { slot: 'beard',    folder: 'Beard',     tag: 'Beard',    prefix: null, anims: ['Idl','Mov','Mel','Mag','Ran'], z: 9 },

  // ── In front of body ────────────────────────────────────────────────────
  { slot: 'cape',     folder: 'Cape',      tag: 'Cape',     prefix: 'F', anims: ['Idl','Mov','Mel','Mag','Ran'], z: 20 },
  { slot: 'quiver',   folder: 'Quiver',    tag: 'Quiver',   prefix: 'F', anims: ['Idl','Mov','Ran'], z: 21 },
  { slot: 'offhand',  folder: 'Offhand',   tag: 'Offhand',  prefix: 'F', anims: ['Idl','Mov','Mel'], z: 22 },
  { slot: 'artifact', folder: 'Artifact',  tag: 'Artifact', prefix: 'F', anims: ['Idl','Mov','Mag'], z: 23 },
  { slot: 'ranged',   folder: 'Bow',       tag: 'Bow',      prefix: 'F', anims: ['Idl','Mov','Ran'], z: 24 },
  { slot: 'magic',    folder: 'Staff',     tag: 'Staff',    prefix: 'F', anims: ['Idl','Mov','Mag'], z: 25 },
  { slot: 'melee',    folder: 'OneHanded', tag: '1H',       prefix: 'F', anims: ['Idl','Mov','Mel'], z: 26 },
];

export function layerUrl(layer, gender, variant, anim) {
  const parts = [];
  if (layer.prefix === 'gender') parts.push(gender);
  else if (layer.prefix)         parts.push(layer.prefix);
  parts.push(anim, layer.tag);
  if (variant) parts.push(variant);
  return `/paperdolls/${layer.folder}/${parts.join('_')}.png`;
}

// Auto-generated from public/paperdolls/ by scripts/gen-paperdoll-manifest.js.
export const VARIANTS = GENERATED_VARIANTS;

// Slot label used in the composer UI
export const SLOT_LABELS = {
  body: 'Body', hair: 'Hair', top: 'Top', bottom: 'Bottom',
  feet: 'Feet', hands: 'Hands', head: 'Head', beard: 'Beard', cape: 'Cape',
  chest: 'Chest', overall: 'Overall',
  melee: 'Melee', ranged: 'Ranged', magic: 'Magic',
  offhand: 'Shield', artifact: 'Artifact', quiver: 'Quiver',
};

// 128×128 per frame, 4 rows (down/left/right/up), 4 or 6 columns.
export const FRAME_SIZE = 128;

// Slots that look reasonable with a flat-fill tint. Metallic sprites
// (weapons/offhand) read as solid blobs when tinted so we exclude them.
export const TINTABLE_SLOTS = new Set([
  'hair', 'top', 'bottom', 'feet', 'hands', 'chest', 'cape', 'head', 'beard',
]);
